import React, { useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Empty, Image, message, Skeleton, Space, Tag, Typography } from "antd";
import { Text } from "react-font";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import ViewConsents from "./ViewConsents";
import { getDatabase, onValue, ref } from "firebase/database";
import { getDownloadURL, getStorage, listAll, ref as storageRef } from "firebase/storage";


const plainOptions = ['Photos', 'Videos', 'Written Stories', 'Name in Visual Assets'];
var db = getDatabase(Firebase);
var storage = getStorage(Firebase);
const moment = require("moment");

const formatDate = (value) => {
	if (!value) return "-";
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("YYYY MMM DD HH:mm") : String(value);
};

const formatValue = (value) => {
	if (value === null || value === undefined || value === "") return "-";
	if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

const isImageAttachment = (url) => /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(decodeURIComponent(String(url || "")));
const isVideoAttachment = (url) => /\.(mp4|webm|ogg|mov)(\?|$)/i.test(decodeURIComponent(String(url || "")));
const HIDDEN_ACTIVITY_FIELDS = new Set([
	"id",
	"lessonActivityID",
	"userID",
	"userId",
	"communityID",
	"communityId",
	"lessonID",
	"lessonId",
	"organisationID",
	"organizationID",
	"orgID",
	"orgId",
	"countryID",
	"countryId",
]);

const getAttachmentName = (url, index) => {
	try {
		const decodedPath = decodeURIComponent(new URL(url).pathname);
		return decodedPath.split("/").filter(Boolean).pop() || `Attachment ${index + 1}`;
	} catch (_error) {
		return `Attachment ${index + 1}`;
	}
};

const ViewActivity = (props) => {

	const isOpen = props.isOpen ?? props.modal ?? false;
	const [organizationArray, organizationsLoading, organizationsError] = useListVals(ref(db, "talmidsdb/organisations"));
	const [userArray, usersLoading, usersError] = useListVals(ref(db, "talmidsdb/users"));
	const [communityArray, communitiesLoading, communitiesError] = useListVals(ref(db, "talmidsdb/communities"));
	const [countryArray, countriesLoading, countriesError] = useListVals(ref(db, "talmidsdb/countries"));
	const [lessonArray, lessonsLoading, lessonsError] = useListVals(ref(db, "talmidsdb/lessons"));
	const activity = props.activity;
	const [selectedConsent, setSelectedConsent] = useState(null);
	const [consentModal, setConsentModal] = useState(false);

	const closeModal = () => {
		if (typeof props.setModal === "function") {
			props.setModal(false);
		} else if (typeof props.toggle === "function") {
			props.toggle();
		}
	};

	const getUserName = (userId) => {
		const user = userArray.find((item) => item.id === userId || item.userID === userId);
		return user ? `${user.firstname || ""} ${user.surname || ""}`.trim() : "";
	};

	const getCommunityName = (communityId) => {
		const community = communityArray.find((item) => item.id === communityId || item.communityID === communityId);
		return community?.name || community?.communityName || "";
	};

	const getLessonName = (lessonId) => {
		const lesson = lessonArray.find((item) => item.id === lessonId || item.lessonID === lessonId || item.ID === lessonId);
		return lesson?.lessonName || lesson?.lesson || "";
	};

	const getOrganizationName = (organizationId) => {
		const organization = organizationArray.find((item) =>
			item.id === organizationId ||
			item.orgID === organizationId ||
			item.organisationID === organizationId
		);
		return organization?.name || organization?.organisationName || "";
	};

	const getCountryName = (countryId) => {
		const country = countryArray.find((item) =>
			item.id === countryId ||
			item.countryID === countryId
		);
		return country?.countryName || country?.name || "";
	};

	const formatFieldLabel = (key) =>
		String(key || "")
			.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
			.replace(/[_-]+/g, " ")
			.replace(/\b\w/g, (letter) => letter.toUpperCase());

	const getReadableFieldValue = (key, value) => {
		if (value === null || value === undefined || value === "") {
			return null;
		}

		switch (key) {
			case "userName":
			case "assigned":
				return value;
			case "userID":
			case "userId":
				return getUserName(value) || null;
			case "communityID":
			case "communityId":
				return getCommunityName(value) || null;
			case "lessonID":
			case "lessonId":
				return getLessonName(value) || null;
			case "organisationID":
			case "organizationID":
			case "orgID":
			case "orgId":
				return getOrganizationName(value) || null;
			case "countryID":
			case "countryId":
				return getCountryName(value) || null;
			default:
				return value;
		}
	};

	const detailRows = [
		["Activity", activity?.activity],
		["Worker", activity?.assigned || getUserName(activity?.userID) || activity?.userID],
		["Community", activity?.community || getCommunityName(activity?.communityID) || activity?.communityID],
		["Lesson", activity?.lesson || getLessonName(activity?.lessonID) || activity?.lessonID],
		["Origin", activity?.origin],
		["Notes", activity?.notes],
		["Time Added", activity?.timeFormatted || formatDate(activity?.timeStamp || activity?.dateTime || activity?.createdAt)],
	];

	const attachmentUrls = Array.isArray(activity?.urls) ? activity.urls.filter(Boolean) : [];
	const consentRequests = Array.isArray(activity?.requests) ? activity.requests.filter((request) => request && request !== "-") : [];
	const allFieldEntries = Object.entries(activity || {})
		.filter(([key]) =>
			!["urls", "requests", "attachmentError", "attachment1", "attachment2", "attachment3"].includes(key)
		)
		.map(([key, value]) => {
			const readableValue = getReadableFieldValue(key, value);
			return {
				key,
				label: formatFieldLabel(key),
				value: readableValue,
			};
		})
		.filter((entry) => !HIDDEN_ACTIVITY_FIELDS.has(entry.key) && entry.value !== null);
	const detailsLoading = organizationsLoading || usersLoading || communitiesLoading || countriesLoading || lessonsLoading;
	const lookupError = organizationsError || usersError || communitiesError || countriesError || lessonsError;

	const fetchConsent = (id, urlArray) => {
		if (id !== "-") {
			//console.log(id);
			onValue(ref(db, "talmidsdb/consents/" + id), (snapshot) => {
				//console.log(snapshot.exists());
				if (snapshot.exists()) {
					var consent = snapshot.val();
					var arrayTemp = [];
					if (consent && consent.shareNameOnVisualAssets) {
						arrayTemp.push(plainOptions[3]);
					}
					if (consent && consent.videoCheckbox) {
						arrayTemp.push(plainOptions[1]);
					}
					if (consent && consent.photoCheckbox) {
						arrayTemp.push(plainOptions[0]);
					}
					if (consent && consent.writtenStoryCheckbox) {
						arrayTemp.push(plainOptions[2]);
					}
					//console.log(consent);
					consent.checkedList = arrayTemp
					consent.urls = urlArray;

					setSelectedConsent(consent);
					setConsentModal(!consentModal);
				} else {
					message.error("The consent appears to be missing or invalid");
				}
			})
		} else {
			message.error("No Feedback found for this activity");
		}
	}

	return (
		<div>
			<ViewConsents modal={consentModal} setModal={setConsentModal} consent={selectedConsent} />
			<Drawer
				title="View Lesson Activity"
				width={800}
				maskClosable
				className="font-Nunito"
				placement="right"
				open={isOpen}
				onClose={closeModal}
			>
				{activity && detailsLoading ? (
					<Skeleton active title paragraph={{ rows: 12 }} />
				) : activity ? (
					<Space direction="vertical" size="large" style={{ width: "100%" }}>
						{lookupError && (
							<Alert
								message="Some reference details could not be loaded. Available activity data is shown below."
								type="warning"
							/>
						)}
						<Descriptions bordered size="small" column={1} title="Summary">
							{detailRows.map(([label, value]) => (
								<Descriptions.Item key={label} label={label}>
									{formatValue(value)}
								</Descriptions.Item>
							))}
						</Descriptions>

						<div>
							<Typography.Title level={5}>Attachments</Typography.Title>
							{activity.attachmentError && (
								<Alert
									message="Some attachments could not be loaded. Available files are shown below."
									type="warning"
									className="mb-3"
								/>
							)}
							{attachmentUrls.length ? (
								<Space wrap size="middle" align="start">
									{attachmentUrls.map((url, index) => {
										const name = getAttachmentName(url, index);
										return (
											<Space key={url} direction="vertical" size="small">
												<Typography.Text>{name}</Typography.Text>
												{isImageAttachment(url) ? (
													<Image
														src={url}
														width={160}
														height={110}
														style={{ objectFit: "cover", border: "1px solid #d9d9d9", padding: 4 }}
														alt={`Lesson activity attachment ${index + 1}`}
													/>
												) : isVideoAttachment(url) ? (
													<video src={url} width={240} controls>
														<track kind="captions" />
													</video>
												) : (
													<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Document attachment" />
												)}
												<Button href={url} target="_blank" rel="noreferrer" download>
													Open / Download
												</Button>
											</Space>
										);
									})}
								</Space>
							) : (
								<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments found" />
							)}
						</div>

						<div>
							<Typography.Title level={5}>Consents</Typography.Title>
							{consentRequests.length ? (
								<Space wrap>
									{consentRequests.map((request, index) => (
										<Text size={300} className="" key={request}>
								{request === "-" || request === "" ? null :
									<a href="#" onClick={() => {

										var urlArray = []
										listAll(storageRef(storage, "talmidsdb/concents/" + request))
											.then((res) => {
												res.items.forEach((itemRef) => {
													// All the items under listRef.
													getDownloadURL(itemRef).then((url) => {
														urlArray.push(url);
													});
													//setArray(urlArray);
												});

											}).catch((error) => {
												// Uh-oh, an error occurred!
											});

										fetchConsent(request, urlArray);

									}}>
										consent-{index + 1}
									</a>}

										</Text>
									))}
								</Space>
							) : (
								<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No consent references found" />
							)}
						</div>

						<Descriptions bordered size="small" column={1} title="All Fields">
							{allFieldEntries.map((entry) => (
								<Descriptions.Item key={entry.key} label={<Tag>{entry.label}</Tag>}>
									{formatValue(entry.value)}
								</Descriptions.Item>
							))}
						</Descriptions>
					</Space>
				) : (
					<Empty description="No lesson activity selected" />
				)}

			</Drawer>
		</div>
	)
}

export default ViewActivity;
